import { Panel, Checkbox, Alert, CreateButton, Spinner, Button } from '@ixicare/ui';

import * as React from 'react';
import classnames from 'classnames';
import DeviceMapOverlay from '../components/DeviceMapOverlay';
import DynamicMap from '~components/Map/DynamicMap';
import styles from './SitePlaneMap.module.css';
import { LegendMapOverlay } from '../components/LegendMapOverlay/LegendMapOverlay';
import PlaneDeviceTable from '../../../components/Tables/DeviceTables/PlaneDevices/PlaneDeviceTable';
import { useSitePlaneMap } from '../hooks/useSitePlaneMap';
import { t } from '~i18n';
import { MapPostEventWrapperRef } from '~components/Map/MapPostEventWrapper';
import { UploadFloorMapModal } from '../components/SiteModals/UploadFloorMapModal';
import SiteStore from '../store/Site';
import { planeDevicesTableConfig } from '../../../components/Tables/DeviceTables/PlaneDevices/tableConfig';
import { CreateInstalledDeviceModal } from '~modules/devices/components/DeviceModals/CreateInstalledDeviceModal';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';
import { useBreadcrumbs } from '~components/DashboardWrapper/Breadcrumbs';

interface SitePlaneMapContainerProps {
  site: SiteStore;
  initialPlaneId: number;
}

/** Renders both raster floorplans and geographic planes; the map picks its renderer from the plane. */
const SitePlaneMapContainer: React.FC<SitePlaneMapContainerProps> = ({ site, initialPlaneId }) => {
  const mapRef = React.useRef<MapPostEventWrapperRef>(null!);
  const [showUpdateModal, setShowUpdateModal] = React.useState(false);
  const {
    plane,
    state,
    dispatch,
    markers,
    planeDbgs,
    selectedDevices,
    selectedDeviceDetail,
    goToDevice,
    setGoToDevice,
    isMoving,
    setIsMoving,
    newLocation,
    setNewLocation,
    isEditingDevice,
    setIsEditingDevice,
    onMarkerPressed,
    onPlaneSelectedEvent,
    handleCloseDetail,
    handleRefresh,
    coordSrid,
  } = useSitePlaneMap({ site, initialPlaneId, mapRef });

  // The floor selector can switch planes without a route change, so the crumbs follow `plane`.
  useBreadcrumbs(plane ? [{ label: `${t('plane.floor')} ${plane.floor}` }, { label: plane.floorName ?? '' }] : []);

  return (
    <>
      {plane && (
        <>
          <Panel.Title>{plane.floorName ?? ''}</Panel.Title>
          <div className="row mb-20">
            {plane.isPlaneImageProcessed && (
              <>
                <div className="col-md-2">
                  <Checkbox
                    id="showLegend"
                    name="showLegend"
                    checked={state.showLegend}
                    label={t('site.floors.legend.showLegend')}
                    onChange={dispatch}
                  />
                </div>
                <div className="col-md-2">
                  <Checkbox
                    id="showRooms"
                    name="showRooms"
                    checked={state.showRooms}
                    label={t('site.floors.legend.showRooms')}
                    onChange={dispatch}
                  />
                </div>
                <div className="col-md-2">
                  <Checkbox
                    id="showRoomLabels"
                    name="showRoomLabels"
                    checked={state.showRoomLabels}
                    label={t('site.floors.legend.showRoomLabels')}
                    onChange={dispatch}
                  />
                </div>
              </>
            )}
            <div className="col-md-2">
              <Checkbox
                id="showBeacons"
                name="showBeacons"
                checked={state.showBeacons}
                label={t('site.floors.legend.showBeacons')}
                onChange={dispatch}
              />
            </div>
            <div className="col-md-2">
              <Checkbox
                id="showGateways"
                name="showGateways"
                checked={state.showGateways}
                label={t('site.floors.legend.showGateways')}
                onChange={dispatch}
              />
            </div>
            <div className="col-md-2">
              <Checkbox
                id="showButtons"
                name="showButtons"
                checked={state.showButtons}
                label={t('site.floors.legend.showButtons')}
                onChange={dispatch}
              />
            </div>
          </div>
          {!!plane.cdnUrl && plane.isPlaneImageProcessed ? (
            <div className="mb-25" style={{ position: 'relative' }}>
              <DynamicMap
                page="geo-map-detail-view"
                siteId={site.id}
                planeUuid={plane?.uuid}
                floorSelect
                showRooms={state.showRooms}
                showRoomLabels={state.showRoomLabels}
                markers={markers}
                onMarkerPressed={onMarkerPressed}
                onPlaneSelectedEvent={onPlaneSelectedEvent}
                onMarkerInsert={setNewLocation}
                goToDevice={goToDevice}
                safeAreaTop={10}
                canInsertMarker={isMoving}
                ref={mapRef}
              />
              {selectedDeviceDetail && (
                <DeviceMapOverlay
                  onMoveStart={() => setIsMoving(true)}
                  onMoveEnd={() => setIsMoving(false)}
                  newLocation={newLocation}
                  isMoving={isMoving}
                  type={selectedDeviceDetail.type}
                  device={selectedDeviceDetail}
                  onClose={handleCloseDetail}
                  onRefresh={handleRefresh}
                  isEditing={isEditingDevice}
                  onEditStart={() => setIsEditingDevice(true)}
                  coordSrid={coordSrid}
                />
              )}

              {state.showLegend && <LegendMapOverlay />}
              {isMoving && newLocation && !selectedDevices && (
                <CreateInstalledDeviceModal
                  siteId={site.id}
                  newLocation={newLocation}
                  onClose={handleCloseDetail}
                  onRefresh={handleRefresh}
                  coordSrid={coordSrid}
                />
              )}
              {selectedDevices && selectedDeviceDetail && isEditingDevice && (
                <UpdateDeviceModal
                  type={selectedDeviceDetail.type}
                  devices={selectedDevices}
                  onClose={handleCloseDetail}
                  onRefresh={handleRefresh}
                />
              )}
              {/* Nothing visibly happens on click until the plane is clicked too, so
                  the armed state gets a standing instruction and a way back out. */}
              {isMoving && !newLocation && !selectedDevices && (
                <div className={styles.InsertHint}>
                  <span className={styles.InsertHintText}>{t('site.floors.placeDeviceHint')}</span>
                  <button type="button" className={styles.InsertHintCancel} onClick={() => setIsMoving(false)}>
                    {t('global.cancel')}
                  </button>
                </div>
              )}
              {!selectedDevices && (
                <CreateButton
                  onClick={() => setIsMoving(!isMoving)}
                  className={classnames(styles.DeviceAddButtonWrapper, {
                    [styles.DeviceAddButtonActive]: isMoving,
                  })}
                />
              )}
            </div>
          ) : !!plane.cdnUrl && !plane.isPlaneImageProcessed ? (
            <div className="mb-20">
              <Alert>{t('site.floors.alert.notProcessed')}</Alert>
              <Spinner />
            </div>
          ) : (
            <>
              <Alert>{t('site.floors.alert.noImage')}</Alert>
              <Button onClick={() => setShowUpdateModal(true)}>{t('site.floors.uploadFloorPlan')}</Button>
            </>
          )}
          {planeDbgs && planeDbgs.length > 0 && (
            <div className="mb-25" style={{ position: 'relative' }}>
              <PlaneDeviceTable
                config={planeDevicesTableConfig}
                plane={plane}
                items={planeDbgs}
                setGoToDevice={setGoToDevice}
                onRefresh={handleRefresh}
              />
            </div>
          )}
          {showUpdateModal && (
            <UploadFloorMapModal
              site={site}
              floor={plane}
              onRefresh={handleRefresh}
              onClose={() => setShowUpdateModal(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default SitePlaneMapContainer;
